import { AdminCoupons } from "../components/AdminCoupons";
import React, { useEffect, useState } from "react";
import api, { formatApiError, formatCurrency } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { PencilSimple, Users, CurrencyCircleDollar, Timer, Warning } from "@phosphor-icons/react";

const STATUS_LABEL = { trial: "Teste", active: "Ativo", past_due: "Pendente", expired: "Expirado", canceled: "Cancelado", owner: "Dono" };
const STATUS_CLASS = { trial: "bg-amber-50 text-amber-700", active: "bg-emerald-50 text-emerald-700", past_due: "bg-rose-50 text-rose-700", expired: "bg-rose-50 text-rose-700", canceled: "bg-slate-100 text-slate-600", owner: "bg-indigo-50 text-indigo-700" };

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="card-crisp p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</span>
        <Icon size={18} weight="duotone" className="text-indigo-600" />
      </div>
      <div className="font-mono-nums text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

export default function AdminPage() {
  const [subs, setSubs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ plan: "profissional", plan_status: "active", plan_until: "" });
  const [filter, setFilter] = useState("");
  const [queue, setQueue] = useState([]);

  const load = () => {
    Promise.all([api.get("/admin/subscribers"), api.get("/admin/metrics"), api.get("/admin/chamador-provisioning")])
      .then(([s, m, q]) => { setSubs(s.data); setMetrics(m.data); setQueue(q.data); })
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(load, []);

  const openEdit = (s) => {
    setEditing(s);
    setForm({ plan: s.plan, plan_status: s.status === "expired" ? "active" : s.status, plan_until: (s.until || new Date().toISOString()).slice(0, 10) });
  };

  const save = async () => {
    try {
      await api.put(`/admin/subscribers/${editing.id}`, { ...form, plan_until: new Date(form.plan_until + "T23:59:59Z").toISOString() });
      toast.success("Acesso atualizado");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const extend = (days) => {
    const base = form.plan_until ? new Date(form.plan_until) : new Date();
    const d = new Date(Math.max(base.getTime(), Date.now()));
    d.setDate(d.getDate() + days);
    setForm({ ...form, plan_until: d.toISOString().slice(0, 10), plan_status: "active" });
  };

  const list = subs.filter((s) => !filter || `${s.name} ${s.email} ${s.clinic_name}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div data-testid="admin-page">
      <PageHeader title="Painel do dono" subtitle="Assinantes, receita recorrente e liberação manual de acesso." />

      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={Users} label="Clientes" value={metrics.total_users} testId="metric-users" />
          <Stat icon={CurrencyCircleDollar} label="MRR (receita mensal)" value={formatCurrency(metrics.mrr)} testId="metric-mrr" />
          <Stat icon={Timer} label="Em teste grátis" value={metrics.by_status.trial || 0} testId="metric-trial" />
          <Stat icon={Warning} label="Expirados / pendentes" value={(metrics.by_status.expired || 0) + (metrics.by_status.past_due || 0)} testId="metric-expired" />
        </div>
      )}

      <div className="mb-4">
        <Input placeholder="Buscar por nome, e-mail ou clínica" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-sm" data-testid="admin-search" />
      </div>

      <div className="card-crisp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-5 py-3 font-medium">Cliente</th>
              <th className="px-5 py-3 font-medium">Clínica</th>
              <th className="px-5 py-3 font-medium">Plano</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Acesso até</th>
              <th className="px-5 py-3 font-medium">Pagamento</th>
              <th className="px-5 py-3 w-16" />
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`subscriber-row-${s.id}`}>
                <td className="px-5 py-3"><div className="font-medium text-slate-900">{s.name}</div><div className="text-xs text-slate-500">{s.email}</div></td>
                <td className="px-5 py-3 text-slate-700">{s.clinic_name || "—"}</td>
                <td className="px-5 py-3">{s.plan_name}{s.combo && <span className="ml-1.5 text-[10px] font-semibold uppercase bg-[#F5ECD6] text-[#8F6E2E] px-1.5 py-0.5 rounded">+Chamador</span>}</td>
                <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_CLASS[s.status]}`}>{STATUS_LABEL[s.status]}</span></td>
                <td className="px-5 py-3 font-mono-nums">{s.until ? new Date(s.until).toLocaleDateString("pt-BR") : "—"}</td>
                <td className="px-5 py-3 text-xs text-slate-600">{s.has_stripe ? "Stripe (automático)" : s.is_owner ? "—" : "Manual / teste"}</td>
                <td className="px-5 py-3 text-right">
                  {!s.is_owner && <button onClick={() => openEdit(s)} className="text-slate-500 hover:text-indigo-600" data-testid={`subscriber-edit-${s.id}`}><PencilSimple size={16} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {queue.length > 0 && (
        <div className="mt-8" data-testid="chamador-queue">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-3">Liberações do Seu Chamador</h3>
          <div className="card-crisp overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600"><tr><th className="px-5 py-3 font-medium">Cliente</th><th className="px-5 py-3 font-medium">Ação</th><th className="px-5 py-3 font-medium">Status</th><th className="px-5 py-3 font-medium">Quando</th><th className="px-5 py-3" /></tr></thead>
              <tbody>
                {queue.map((q) => (
                  <tr key={q.id} className="border-t border-slate-100" data-testid={`chamador-row-${q.id}`}>
                    <td className="px-5 py-3"><div className="font-medium text-slate-900">{q.payload?.name}</div><div className="text-xs text-slate-500">{q.email} · {q.payload?.clinic_name}</div></td>
                    <td className="px-5 py-3">{q.active ? "Ativar" : "Desativar"}</td>
                    <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${q.status === "sent" || q.status === "done_manual" ? "bg-emerald-50 text-emerald-700" : q.status === "failed" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{{ sent: "Enviado automaticamente", done_manual: "Liberado manualmente", failed: "Falhou", pending_manual: "Aguardando liberação manual" }[q.status] || q.status}</span><div className="text-xs text-slate-400 mt-0.5">{q.detail}</div></td>
                    <td className="px-5 py-3 text-slate-500 font-mono-nums">{new Date(q.created_at).toLocaleString("pt-BR")}</td>
                    <td className="px-5 py-3 text-right">{(q.status === "pending_manual" || q.status === "failed") && <Button size="sm" variant="outline" onClick={async () => { await api.post(`/admin/chamador-provisioning/${q.user_id}/done`); load(); toast.success("Marcado como liberado"); }} data-testid={`chamador-done-${q.id}`}>Marcar liberado</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AdminCoupons />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Liberar acesso — {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Plano</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger className="mt-1" data-testid="admin-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="essencial">Essencial</SelectItem>
                  <SelectItem value="profissional">Profissional</SelectItem>
                  <SelectItem value="clinica">Clínica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.plan_status} onValueChange={(v) => setForm({ ...form, plan_status: v })}>
                <SelectTrigger className="mt-1" data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="trial">Teste grátis</SelectItem>
                  <SelectItem value="canceled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Acesso até</Label>
              <Input type="date" value={form.plan_until} onChange={(e) => setForm({ ...form, plan_until: e.target.value })} className="mt-1" data-testid="admin-until" />
              <div className="flex gap-2 mt-2">
                {[["+7 dias", 7], ["+30 dias", 30], ["+1 ano", 365]].map(([l, d]) => (
                  <Button key={d} type="button" size="sm" variant="outline" onClick={() => extend(d)} data-testid={`admin-extend-${d}`}>{l}</Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700" data-testid="admin-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
